import {
  ASTEROID_WALL_RESTITUTION, ASTEROID_WALL_SPIN_COUPLING, ASTEROID_WALL_TANGENTIAL_FRICTION,
  BULLET_RADIUS, TICK_MS
} from 'asteroids-shared/constants';
import {
  clamp,
  indexById,
  lerpAngleDeg,
  lerpWrappedWithPad,
  torusCanon
} from 'asteroids-shared/helpers';
import {
  asteroidWallSpinMassFactor,
  bounceCircleOffWalls,
  normalizeDeg,
  wrap
} from 'asteroids-shared/physics';

// Max ms past newest snap to integrate bullets/rocks when extrapolating.
export const DEFAULT_EXTRAPOLATE_AHEAD_LIMIT_MS = 60;

// Ring buffer; sample(renderTime) lerps remote entities (server clock ms).
export default class SnapshotInterpolator {
  constructor({
    worldWidth,
    worldHeight,
    toroidalMap = true,
    historyMs = 600,
    extrapolateAheadLimitMs = DEFAULT_EXTRAPOLATE_AHEAD_LIMIT_MS
  }) {
    this.worldWidth = worldWidth;
    this.worldHeight = worldHeight;
    this.toroidalMap = toroidalMap !== false;
    this.historyMs = historyMs;
    this.extrapolateAheadLimitMs = extrapolateAheadLimitMs;
    this.snapshots = [];
    this.destroyedShips = new Set();
    this.destroyedBullets = new Set();
    this.view = { ships: [], asteroids: [], bullets: [], modeState: null, sampleRenderTime: NaN };
  }

  noteBulletDestroyed(id) {
    this.destroyedBullets.add(id);
  }

  push(snap) {
    if (!Number.isFinite(snap.serverTime)) return;
    // Ignore out-of-order snapshots.
    const prev = this.snapshots.at(-1);
    if (prev && snap.serverTime <= prev.serverTime) return;

    snap.shipsById     = indexById(snap.ships);
    snap.asteroidsById = indexById(snap.asteroids);
    snap.bulletsById   = indexById(snap.bullets);

    if (prev) {
      for (const s of prev.ships) if (!snap.shipsById.has(s.id)) this.destroyedShips.add(s.id);
      for (const b of prev.bullets) if (!snap.bulletsById.has(b.id)) this.destroyedBullets.add(b.id);
    }

    this.snapshots.push(snap);
    const cutoff = snap.serverTime - this.historyMs;
    while (this.snapshots.length > 2 && this.snapshots[0].serverTime < cutoff) {
      this.snapshots.shift();
    }
    this.#pruneTombstones();
  }

  latest() { return this.snapshots.at(-1) ?? null; }

  oldest() { return this.snapshots[0] ?? null; }

  // Interpolate at renderTime (server ms). opts.extrapolateAheadLimitMs overrides ctor cap past newest snap.
  sample(renderTime, opts = {}) {
    const snaps = this.snapshots;
    if (snaps.length === 0) {
      return { ships: [], asteroids: [], bullets: [], modeState: null, sampleRenderTime: NaN };
    }
    if (!Number.isFinite(renderTime)) {
      const fallback = snaps.at(-1)?.serverTime;
      renderTime = Number.isFinite(fallback) ? fallback : snaps[0].serverTime;
    }
    if (snaps.length === 1) {
      const s = snaps[0];
      const W = this.worldWidth;
      const H = this.worldHeight;
      return {
        ships:     filterIfAny(s.ships, this.destroyedShips),
        asteroids: s.asteroids.map((a) => (
          this.toroidalMap
            ? { ...a, x: torusCanon(a.x, W), y: torusCanon(a.y, H) }
            : { ...a }
        )),
        bullets:   filterIfAny(s.bullets, this.destroyedBullets),
        modeState: s.modeState ?? null,
        sampleRenderTime: renderTime
      };
    }
    // Pick pair (a, b) bracketing renderTime: last index whose next snap is still below playhead, then step forward one segment.
    const lo = snaps.findLastIndex((_, k) => k < snaps.length - 1 && snaps[k + 1].serverTime < renderTime)
    const i = lo < 0 ? 0 : Math.min(lo + 1, snaps.length - 2)
    const a = snaps[i];
    const b = snaps[i + 1];
    const span = b.serverTime - a.serverTime;

    // Past newest: follow last (a→b) segment; default cap so long WS gaps do not run unbounded.
    const rawAhead = Math.max(0, renderTime - b.serverTime);
    const capMs = opts.extrapolateAheadLimitMs !== undefined
      ? opts.extrapolateAheadLimitMs
      : this.extrapolateAheadLimitMs
    const maxAhead = capMs === null || capMs === Infinity
      ? Number.POSITIVE_INFINITY
      : capMs
    const cappedAhead = Math.min(rawAhead, maxAhead);
    const effectiveRenderTime = renderTime - (rawAhead - cappedAhead);

    const t = span > 0
      ? Math.max(0, (effectiveRenderTime - a.serverTime) / span)
      : 0

    return {
      ships: this.#interpWrapped(a.shipsById, b.ships, t, a.serverTime, b.serverTime, effectiveRenderTime, {
        withRotation: true,
        padFor: () => 0,
        skipIds: this.destroyedShips
      }),
      asteroids: this.#interpAsteroidsLerp(a, b, effectiveRenderTime, t),
      bullets: this.#interpBulletsLerp(a, b, t, effectiveRenderTime),
      modeState: b.modeState ?? a.modeState ?? null,
      sampleRenderTime: effectiveRenderTime
    };
  }

  commitView(renderTime) {
    this.view = Number.isFinite(renderTime)
      ? this.sample(renderTime)
      : { ships: [], asteroids: [], bullets: [], modeState: null, sampleRenderTime: NaN };
  }

  // CV bullet position from spawn keyframe to renderServerTime (tick integration).
  bulletPositionAt(ent, startServerTime, renderServerTime) {
    return this.#bulletIntegratedPosition(
      ent,
      startServerTime,
      renderServerTime,
      this.worldWidth,
      this.worldHeight
    );
  }

  #bulletIntegratedPosition(ent, startServerTime, renderServerTime, W, H) {
    const spawn = { x: ent.x, y: ent.y };
    if (!Number.isFinite(renderServerTime) || !Number.isFinite(startServerTime)) return spawn;
    // Do not integrate backward before spawn: -velocity from the nose reads as slug sliding from stern toward muzzle.
    if (renderServerTime <= startServerTime) return spawn;
    let el = (renderServerTime - startServerTime) / TICK_MS;
    const MAX_EL = 64;
    if (el > MAX_EL) el = MAX_EL;
    if (el < 1e-12) return spawn;
    const vx = ent.vx ?? 0;
    const vy = ent.vy ?? 0;
    const p = { x: spawn.x, y: spawn.y };
    const full = Math.floor(el);
    const frac = el - full;
    const clampToBounds = () => {
      if (p.x < BULLET_RADIUS) {
        p.x = BULLET_RADIUS;
        return true;
      }
      if (p.x > W - BULLET_RADIUS) {
        p.x = W - BULLET_RADIUS;
        return true;
      }
      if (p.y < BULLET_RADIUS) {
        p.y = BULLET_RADIUS;
        return true;
      }
      if (p.y > H - BULLET_RADIUS) {
        p.y = H - BULLET_RADIUS;
        return true;
      }
      return false;
    };
    for (let i = 0; i < full; i++) {
      p.x += vx;
      p.y += vy;
      if (this.toroidalMap) {
        wrap(p, W, H, BULLET_RADIUS);
      } else if (clampToBounds()) {
        return p;
      }
    }
    if (frac > 1e-9) {
      p.x += vx * frac;
      p.y += vy * frac;
      if (this.toroidalMap) {
        wrap(p, W, H, BULLET_RADIUS);
      } else {
        clampToBounds();
      }
    }
    return p;
  }

  #interpBulletsLerp(aSnap, bSnap, t, effectiveRenderTime) {
    const out = [];
    const aMap = aSnap.bulletsById;
    const W = this.worldWidth;
    const H = this.worldHeight;
    for (const be of bSnap.bullets) {
      if (this.destroyedBullets.has(be.id)) continue;
      const ae = aMap.get(be.id);
      if (!ae) {
        // New this segment: do not pin to `be` for the whole [Ta,Tb] (that teleports the slug
        // ahead of the ship until `ae` exists, then lerp snaps it back — debug red “ricochet”).
        if (!Number.isFinite(effectiveRenderTime) || effectiveRenderTime < bSnap.serverTime) continue;
        const p = this.#bulletIntegratedPosition(be, bSnap.serverTime, effectiveRenderTime, W, H);
        out.push({ ...be, x: p.x, y: p.y });
        continue;
      }
      if (this.toroidalMap === false) {
        out.push({
          ...be,
          x: ae.x + (be.x - ae.x) * t,
          y: ae.y + (be.y - ae.y) * t
        });
        continue;
      }
      out.push({
        ...be,
        x: lerpWrappedWithPad(ae.x, be.x, t, W, 0),
        y: lerpWrappedWithPad(ae.y, be.y, t, H, 0)
      });
    }
    return out;
  }

  #asteroidRotationAt(ae, be, aTime, bTime, renderTime) {
    if (ae.rotation == null || be.rotation == null) return {};
    const span = bTime - aTime;
    if (renderTime <= bTime) {
      const u = span > 0 ? clamp((renderTime - aTime) / span, 0, 1) : 1;
      return { rotation: lerpAngleDeg(ae.rotation, be.rotation, u) };
    }
    let extra = (renderTime - bTime) / TICK_MS;
    const MAX_EL = 64;
    if (extra > MAX_EL) extra = MAX_EL;
    const rs = be.rotationSpeed ?? 0;
    return { rotation: normalizeDeg(be.rotation + rs * extra) };
  }

  #asteroidIntegratedPosition(ent, startServerTime, renderServerTime, W, H) {
    if (this.toroidalMap) {
      let el = (renderServerTime - startServerTime) / TICK_MS;
      const MAX_EL = 64;
      if (el > MAX_EL) el = MAX_EL;
      if (el <= 0) return { x: torusCanon(ent.x, W), y: torusCanon(ent.y, H) };
      const p = { x: ent.x, y: ent.y };
      const vx = ent.vx ?? 0;
      const vy = ent.vy ?? 0;
      const full = Math.floor(el);
      const frac = el - full;
      for (let i = 0; i < full; i++) {
        p.x = torusCanon(p.x + vx, W);
        p.y = torusCanon(p.y + vy, H);
      }
      if (frac > 1e-9) {
        p.x = torusCanon(p.x + vx * frac, W);
        p.y = torusCanon(p.y + vy * frac, H);
      }
      return { x: p.x, y: p.y };
    }

    const state = {
      position: { x: ent.x, y: ent.y },
      velocity: { x: ent.vx ?? 0, y: ent.vy ?? 0 },
      rotation: ent.rotation ?? 0,
      rotationSpeed: ent.rotationSpeed ?? 0,
      radius: ent.radius ?? 0
    };
    let el = (renderServerTime - startServerTime) / TICK_MS;
    const MAX_EL = 64;
    if (el > MAX_EL) el = MAX_EL;
    if (el <= 0) return state;
    const bounds = { width: W, height: H, toroidalMap: false };
    const full = Math.floor(el);
    const frac = el - full;
    for (let i = 0; i < full; i++) {
      state.position.x += state.velocity.x;
      state.position.y += state.velocity.y;
      state.rotation = normalizeDeg(state.rotation + state.rotationSpeed);
      bounceCircleOffWalls(state, bounds, {
        restitution: ASTEROID_WALL_RESTITUTION,
        tangentialFriction: ASTEROID_WALL_TANGENTIAL_FRICTION,
        spinCoupling: ASTEROID_WALL_SPIN_COUPLING,
        spinMassFactor: asteroidWallSpinMassFactor(state.radius),
        spinDamping: 0.985
      });
    }
    if (frac > 1e-9) {
      state.position.x += state.velocity.x * frac;
      state.position.y += state.velocity.y * frac;
      state.rotation = normalizeDeg(state.rotation + state.rotationSpeed * frac);
      bounceCircleOffWalls(state, bounds, {
        restitution: ASTEROID_WALL_RESTITUTION,
        tangentialFriction: ASTEROID_WALL_TANGENTIAL_FRICTION,
        spinCoupling: ASTEROID_WALL_SPIN_COUPLING,
        spinMassFactor: asteroidWallSpinMassFactor(state.radius),
        spinDamping: 0.985
      });
    }
    return state;
  }

  #interpAsteroidsLerp(aSnap, bSnap, renderTime, t) {
    const out = [];
    const aMap = aSnap.asteroidsById;
    const aTime = aSnap.serverTime;
    const bTime = bSnap.serverTime;
    const W = this.worldWidth;
    const H = this.worldHeight;
    for (const be of bSnap.asteroids) {
      const ae = aMap.get(be.id);
      if (!ae) {
        out.push({
          ...be,
          ...(this.toroidalMap
            ? { x: torusCanon(be.x, W), y: torusCanon(be.y, H) }
            : {}),
          ...this.#asteroidRotationAt(be, be, bTime, bTime, renderTime)
        });
        continue;
      }
      if (this.toroidalMap === false) {
        const fromA = renderTime <= bTime;
        const base = fromA ? ae : be;
        const start = fromA ? aTime : bTime;
        const raw = this.#asteroidIntegratedPosition(base, start, renderTime, W, H);
        out.push({
          ...be,
          x: raw.position?.x ?? raw.x,
          y: raw.position?.y ?? raw.y,
          rotation: raw.rotation ?? be.rotation,
          rotationSpeed: raw.rotationSpeed ?? be.rotationSpeed
        });
        continue;
      }
      out.push({
        ...be,
        x: lerpWrappedWithPad(ae.x, be.x, t, W, 0),
        y: lerpWrappedWithPad(ae.y, be.y, t, H, 0),
        ...this.#asteroidRotationAt(ae, be, aTime, bTime, renderTime)
      });
    }
    return out;
  }

  // Ship rotation: av integration when turning, else angle lerp; past bTime extrapolate with be.av.
  #shipRotationAt(ae, be, aTime, bTime, renderTime) {
    const span = bTime - aTime;
    if (span <= 1e-6) return Number.isFinite(be.rotation) ? be.rotation : ae.rotation;
    if (renderTime >= bTime) {
      const avB = be.av ?? 0;
      const past = renderTime - bTime;
      return normalizeDeg(be.rotation + avB * (past / TICK_MS));
    }
    const avA = ae.av ?? 0;
    const avB = be.av ?? 0;
    const useAv = Math.abs(avA) > 1e-6 || Math.abs(avB) > 1e-6;
    if (!useAv) {
      return normalizeDeg(lerpAngleDeg(ae.rotation, be.rotation, (renderTime - aTime) / span));
    }
    let heading = ae.rotation;
    let wallT = aTime;
    const end = Math.min(renderTime, bTime);
    while (wallT + TICK_MS <= end + 1e-9) {
      const u = (wallT - aTime) / span;
      const av = avA + (avB - avA) * clamp(u, 0, 1);
      heading = normalizeDeg(heading + av);
      wallT += TICK_MS;
    }
    const frac = (end - wallT) / TICK_MS;
    if (frac > 1e-6) {
      const u = (wallT - aTime) / span;
      const av = avA + (avB - avA) * clamp(u, 0, 1);
      heading = normalizeDeg(heading + av * frac);
    }
    return normalizeDeg(heading);
  }

  #pruneTombstones() {
    if (!this.destroyedShips.size && !this.destroyedBullets.size) return;
    const liveShips = new Set();
    const liveBullets = new Set();
    for (const s of this.snapshots) {
      for (const sh of s.ships) liveShips.add(sh.id);
      for (const b of s.bullets) liveBullets.add(b.id);
    }
    for (const id of this.destroyedShips) if (!liveShips.has(id)) this.destroyedShips.delete(id);
    for (const id of this.destroyedBullets) if (!liveBullets.has(id)) this.destroyedBullets.delete(id);
  }

  #interpWrapped(aMap, bArr, t, aTime, bTime, renderTime, { withRotation, padFor, skipIds }) {
    const out = [];
    const W = this.worldWidth;
    const H = this.worldHeight;
    for (const be of bArr) {
      if (skipIds?.has(be.id)) continue;
      const ae = aMap.get(be.id);
      if (!ae) { out.push(be); continue; }
      // Ships: do not lerp position across death/respawn — authority flips `alive` and x/y are unrelated poses.
      if (Object.hasOwn(ae, 'alive') && Object.hasOwn(be, 'alive') && ae.alive !== be.alive) {
        out.push({
          ...be,
          x: this.toroidalMap ? torusCanon(be.x, W) : be.x,
          y: this.toroidalMap ? torusCanon(be.y, H) : be.y,
          ...(withRotation && be.rotation != null
            ? { rotation: this.#shipRotationAt(be, be, bTime, bTime, renderTime) }
            : {})
        });
        continue;
      }
      if (this.toroidalMap === false) {
        out.push({
          ...be,
          x: ae.x + (be.x - ae.x) * t,
          y: ae.y + (be.y - ae.y) * t,
          ...(withRotation && ae.rotation != null
            ? { rotation: this.#shipRotationAt(ae, be, aTime, bTime, renderTime) }
            : {})
        });
        continue;
      }
      const pad = Math.max(padFor?.(ae) ?? 0, padFor?.(be) ?? 0);
      out.push({
        ...be,
        x: lerpWrappedWithPad(ae.x, be.x, t, W, pad),
        y: lerpWrappedWithPad(ae.y, be.y, t, H, pad),
        ...(withRotation && ae.rotation != null
          ? { rotation: this.#shipRotationAt(ae, be, aTime, bTime, renderTime) }
          : {})
      });
    }
    return out;
  }
}

function filterIfAny(arr, skipIds) {
  if (!skipIds.size) return arr;
  return arr.filter((e) => !skipIds.has(e.id))
}
